import React from 'react'
import { theme } from '../theme'

/**
 * Bulk quality re-measure, for runs that have a stored received fax but no score.
 *
 * This is a UI action rather than an operations script for a concrete reason: the
 * application runs on Fargate, so the deploy host has neither the app's runtime
 * credentials nor VPC access to RDS. A maintenance action that cannot be run from
 * anywhere the data lives is not a usable action, so it belongs where the data is.
 *
 * Counts before it writes. The first click is a dry run that reports how many runs
 * qualify; only the second click writes anything. That ordering exists because "re-measure
 * everything" is easy to click by accident and the count is the only thing that tells you
 * whether you are about to touch three runs or three hundred.
 */
export function BulkRescore() {
  const [phase, setPhase] = React.useState<'idle' | 'counted' | 'running' | 'done'>('idle')
  const [count, setCount] = React.useState<number>(0)
  const [result, setResult] = React.useState<string | null>(null)
  const [err, setErr] = React.useState<string | null>(null)

  async function call(dryRun: boolean) {
    setErr(null)
    try {
      const r = await fetch(`/api/runs/rescore-quality/bulk?dry_run=${dryRun}&limit=200`, {
        method: 'POST',
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.detail || `HTTP ${r.status}`)
      }
      return await r.json()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
      return null
    }
  }

  async function count_() {
    const d = await call(true)
    if (!d) return
    setCount(d.candidates ?? 0)
    setPhase('counted')
  }

  async function apply() {
    setPhase('running')
    const d = await call(false)
    if (!d) {
      setPhase('counted')
      return
    }
    setResult(
      `${d.scored} scored, ${d.unscorable} carried no target, ${d.skipped} skipped ` +
        `(of ${d.candidates} examined)`,
    )
    setPhase('done')
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
      {phase === 'idle' && (
        <button
          onClick={count_}
          style={{
            padding: '0.4rem 0.8rem', fontSize: '0.8rem', background: theme.bgAlt,
            color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4,
            cursor: 'pointer',
          }}
          title="Find runs that have a received fax stored but no quality score"
        >
          Find unscored runs
        </button>
      )}

      {phase === 'counted' && (
        <>
          <span style={{ fontSize: '0.8rem', color: '#9aa0a6' }}>
            {count === 0
              ? 'No unscored runs with a stored fax.'
              : `${count} run${count === 1 ? '' : 's'} can be measured.`}
          </span>
          {count > 0 && (
            <button
              onClick={apply}
              style={{
                padding: '0.4rem 0.9rem', fontSize: '0.8rem', background: theme.primary,
                color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Measure them
            </button>
          )}
          <button
            onClick={() => setPhase('idle')}
            style={{
              padding: '0.4rem 0.7rem', fontSize: '0.8rem', background: 'transparent',
              color: '#9aa0a6', border: `1px solid ${theme.border}`, borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </>
      )}

      {phase === 'running' && (
        <span style={{ fontSize: '0.8rem', color: '#9aa0a6' }}>
          Measuring {count} run{count === 1 ? '' : 's'}… this runs one at a time so it
          cannot slow live fax processing.
        </span>
      )}

      {phase === 'done' && (
        <>
          <span style={{ fontSize: '0.8rem', color: '#4caf50' }}>{result}</span>
          <button
            onClick={() => { setPhase('idle'); setResult(null) }}
            style={{
              padding: '0.4rem 0.7rem', fontSize: '0.8rem', background: 'transparent',
              color: '#9aa0a6', border: `1px solid ${theme.border}`, borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Done
          </button>
        </>
      )}

      {err && <span style={{ fontSize: '0.78rem', color: '#ef9a9a' }}>{err}</span>}
    </div>
  )
}
